import {
  Badge,
  Collapse,
  Flex,
  Text,
  ThemeIcon,
  UnstyledButton,
  useMantineColorScheme,
} from "@mantine/core";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router";

import classes from "./styles/MobileNavigation.module.css";

import type { UserMenuItem } from "@/types/const";
import type { FC } from "react";

interface MobileNavLinkProps {
  menuItem: UserMenuItem;
  onNavigate: () => void;
}

const MobileNavLink: FC<MobileNavLinkProps> = ({ menuItem, onNavigate }) => {
  const navigate = useNavigate();
  const [opened, setOpened] = useState(false);
  const { colorScheme } = useMantineColorScheme();

  const isDark = colorScheme === "dark";

  const handleClick = () => {
    if (menuItem.onClick) {
      menuItem.onClick();
      onNavigate();
      return;
    }

    if (menuItem.to) {
      navigate(menuItem.to);
      onNavigate();
      return;
    }

    if (menuItem.isDropdown && menuItem.children) {
      setOpened((o) => !o);
    }
  };

  const content = (
    <Flex align="center" justify="space-between" w="100%">
      <Flex align="center" gap="md">
        {!menuItem.hideIcons && (
          <ThemeIcon
            variant="transparent"
            c={
              menuItem.isLogout
                ? "light-dark(var(--mantine-color-danger-6), var(--mantine-color-red-text))"
                : isDark
                  ? "primaryDark.2"
                  : "dark"
            }
            radius="xl"
            className={menuItem.isLogout ? classes.redColorText : undefined}
            size="lg"
          >
            {menuItem.icon}
          </ThemeIcon>
        )}
        <Text
          size="lg"
          className={menuItem.isLogout ? classes.redColorText : undefined}
          style={{ paddingLeft: menuItem.hideIcons ? 32 : 0 }}
        >
          {menuItem.label}
        </Text>
      </Flex>

      <Flex align="center" gap="xs">
        {menuItem.numericIndicator !== undefined && menuItem.numericIndicator > 0 && (
          <Badge
            color={
              isDark
                ? menuItem.colorIndicator === "light"
                  ? "primary.5"
                  : menuItem.colorIndicator || "danger.5"
                : menuItem.colorIndicator === "light"
                  ? "primary.0"
                  : menuItem.colorIndicator || "danger.6"
            }
            variant="filled"
            radius="lg"
          >
            {menuItem.numericIndicator}
          </Badge>
        )}
        {menuItem.isDropdown &&
          (opened ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />)}
      </Flex>
    </Flex>
  );

  return (
    <>
      <UnstyledButton w="100%" onClick={handleClick}>
        {content}
      </UnstyledButton>

      {menuItem.children && (
        <Collapse in={opened}>
          <Flex
            direction="column"
            pl="lg"
            styles={{
              root: {
                position: "relative",
                top: "-4px",
              },
            }}
          >
            {menuItem.children.map((child, index) => (
              <MobileNavLink key={index} menuItem={child} onNavigate={onNavigate} />
            ))}
          </Flex>
        </Collapse>
      )}
    </>
  );
};

export default MobileNavLink;
